import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useFormik } from "formik";
import * as Yup from "yup";
import { Modal, Select } from "antd";
import { updateMerchantBusinessProfile } from "../../../services/merchantService";
import { getAllCategories } from "../../../services/offerService";
import Input from "../../common/FormInputs";
import PhoneFormInput from "../../common/PhoneInput";
import InputTextArea from "../../common/InputTextArea";
import Selector from "../../common/Selector";
import GoogleMaps from "../../common/GoogleMaps";
import Loader from "../../common/Loader";
import closeIcon from "../../../Assets/Icons/CloseIconSvg.svg";
import { ReactComponent as DownArrow } from "../../../Assets/Icons/greyDownArrow.svg";
import "../../common/OfferForm/offerform.scss";

const EditMerchantModal = ({ merchantDetails, onClose, onSuccess }) => {
    const dispatch = useDispatch();
    const [loading, setLoading] = useState(false);
    const [categories, setCategories] = useState([]);
    const [categoriesLoading, setCategoriesLoading] = useState(false);

    useEffect(() => {
        const fetchCategories = async () => {
            setCategoriesLoading(true);
            try {
                const res = await dispatch(getAllCategories()).unwrap();
                if (res?.success && res?.data) {
                    setCategories(res.data);
                }
            } catch (error) {
                console.error("Failed to fetch categories:", error);
            } finally {
                setCategoriesLoading(false);
            }
        };
        fetchCategories();
    }, [dispatch]);

    // Update categories once loaded to prevent ID flash
    useEffect(() => {
        if (categories.length > 0 && merchantDetails?.category) {
            const categoryIds = Array.isArray(merchantDetails.category)
                ? merchantDetails.category.map(c => typeof c === 'string' ? c : (c?._id || c?.id))
                : [merchantDetails.category];

            // Only set if we haven't set it yet or if it's different (shallow check usually enough for IDs)
            if (formik.values.category.length === 0) {
                formik.resetForm({
                    values: {
                        ...formik.values,
                        category: categoryIds
                    }
                });
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [categories, merchantDetails]);

    const formik = useFormik({
        initialValues: {
            businessName: merchantDetails?.businessName || "",
            website: merchantDetails?.website || merchantDetails?.websiteUrl || "",
            contactPersonName: merchantDetails?.contactPersonName || merchantDetails?.contactName || merchantDetails?.name || "",
            contactPersonNumber: merchantDetails?.contactPersonNumber || merchantDetails?.phone || merchantDetails?.phoneNumber || "",
            address: merchantDetails?.address || merchantDetails?.location || "",
            aboutUs: merchantDetails?.aboutUs || merchantDetails?.description || "",
            // Initialize as empty to prevent ID flash
            category: [],
            locationLat: merchantDetails?.locations?.[0]?.lat || merchantDetails?.locationLat || null,
            locationLng: merchantDetails?.locations?.[0]?.lng || merchantDetails?.locationLng || null,
            locationCity: merchantDetails?.locations?.[0]?.city || merchantDetails?.locationCity || "",
            locationState: merchantDetails?.locations?.[0]?.state || merchantDetails?.locationState || "",
            locationCountry: merchantDetails?.locations?.[0]?.country || merchantDetails?.locationCountry || "",
            locationZip: merchantDetails?.locations?.[0]?.zipCode || merchantDetails?.locationZip || "",
            googlePlaceId: merchantDetails?.locations?.[0]?.googlePlaceId || merchantDetails?.googlePlaceId || "",
            isOnlineMerchant: merchantDetails?.onlineMerchant === true || (merchantDetails?.address === "Australia" && merchantDetails?.onlineMerchant === undefined),
        },
        validationSchema: Yup.object({
            businessName: Yup.string().required("Business name is required"),
            website: Yup.string().url("Invalid URL format").required("Website URL is required"),
            contactPersonName: Yup.string().required("Contact person name is required"),
            contactPersonNumber: Yup.string().required("Business number is required"),
            category: Yup.array().min(1, "Select at least one category").required("Category is required"),
            aboutUs: Yup.string().required("Business description is required"),
            address: Yup.string().when('isOnlineMerchant', {
                is: false,
                then: () => Yup.string().required("Business Address is required"),
                otherwise: () => Yup.string().notRequired(),
            }),
        }),
        onSubmit: async (values) => {
            setLoading(true);
            try {
                const payload = {
                    id: merchantDetails._id,
                    data: {
                        businessName: values.businessName,
                        website: values.website,
                        contactPersonName: values.contactPersonName,
                        contactPersonNumber: values.contactPersonNumber,
                        address: values.isOnlineMerchant ? "Australia" : values.address,
                        aboutUs: values.aboutUs,
                        description: values.aboutUs,
                        category: values.category,
                        onlineMerchant: values.isOnlineMerchant,
                        ...(values.isOnlineMerchant
                            ? {
                                locations: [
                                    {
                                        address: "Australia",
                                        city: "",
                                        state: "",
                                        country: "Australia",
                                        zipCode: "",
                                        lat: -33.8688,
                                        lng: 151.2093,
                                    }
                                ]
                            }
                            : (values.locationLat && values.locationLng
                                ? {
                                    locations: [
                                        {
                                            address: values.address,
                                            city: values.locationCity,
                                            state: values.locationState,
                                            country: values.locationCountry || "Australia",
                                            zipCode: values.locationZip,
                                            lat: Number(values.locationLat),
                                            lng: Number(values.locationLng),
                                        }
                                    ]
                                }
                                : {})
                        )
                    }
                };

                const response = await dispatch(updateMerchantBusinessProfile(payload)).unwrap();
                if (response?.success) {
                    onSuccess && onSuccess();
                    onClose();
                }
            } catch (error) {
                console.error("Failed to update merchant info:", error);
            } finally {
                setLoading(false);
            }
        }
    });

    const handleLocationSelect = (selectedLocation) => {
        formik.setFieldValue("address", selectedLocation?.placeName);
        formik.setFieldValue("locationLat", selectedLocation?.lat);
        formik.setFieldValue("locationLng", selectedLocation?.lng);
        formik.setFieldValue("locationCity", selectedLocation?.city || "");
        formik.setFieldValue("locationState", selectedLocation?.state || "");
        formik.setFieldValue("locationCountry", selectedLocation?.country || "");
        formik.setFieldValue("locationZip", selectedLocation?.zipCode || "");
        formik.setFieldValue("googlePlaceId", selectedLocation?.placeId || "");
    };

    const [showExitConfirm, setShowExitConfirm] = useState(false);

    const handleCloseAttempt = () => {
        if (formik.dirty) {
            setShowExitConfirm(true);
        } else {
            onClose();
        }
    };

    return (
        <div className="fixed inset-0 z-[1000] flex items-center justify-center bg-black bg-opacity-50 py-10">
            <style>
                {`
                @keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }
                @keyframes slideIn { from { transform: translateY(-20px) scale(0.95); opacity: 0; } to { transform: translateY(0) scale(1); opacity: 1; } }
                .edit-merchant-modal { scrollbar-width: none; -ms-overflow-style: none; }
                .edit-merchant-modal::-webkit-scrollbar { display: none; }
                
                /* Custom styles for the category selector chips */
                .edit-merchant-selector .ant-select-selection-item {
                    height: 32px !important;
                    display: flex !important;
                    align-items: center !important;
                    margin: 4px 4px 4px 0 !important;
                    background: #00ADEF !important;
                    border: none !important;
                    border-radius: 50px !important;
                    color: white !important;
                    padding-inline-start: 14px !important;
                    padding-inline-end: 10px !important;
                    font-size: 13px !important;
                    font-weight: 500 !important;
                }
                .edit-merchant-selector .ant-select-selection-item-content {
                    color: white !important;
                    margin-inline-end: 8px !important;
                }
                .edit-merchant-selector .ant-select-selection-item-remove {
                    color: white !important;
                    display: flex !important;
                    align-items: center !important;
                    opacity: 1 !important;
                }
                .edit-merchant-selector .ant-select-selection-item-remove:hover {
                    color: white !important;
                    opacity: 0.8 !important;
                }
                .edit-merchant-selector .ant-select-selection-overflow {
                    display: flex !important;
                    flex-wrap: wrap !important;
                    align-items: center !important;
                    padding: 2px 0 !important;
                }
                .edit-merchant-selector .ant-select-selector {
                    padding: 2px 11px !important;
                    min-height: 45px !important;
                    height: auto !important;
                    border-radius: 5px !important;
                    display: flex !important;
                    align-items: center !important;
                    border: 1px solid #D9D9D9 !important;
                }
                .edit-merchant-selector .ant-select-selection-search {
                    display: flex !important;
                    align-items: center !important;
                    margin-inline-start: 0 !important;
                }
                `}
            </style>
            <div className="w-[95%] max-w-[700px] relative">
                {/* Close Icon: Inside on mobile (top-4 right-4), Outside on desktop (-top-5 -right-5) */}
                <img
                    src={closeIcon}
                    alt="Close"
                    className="absolute top-4 right-4 md:-top-5 md:-right-5 w-[30px] h-[30px] md:w-[35px] md:h-[35px] cursor-pointer z-[10001] transition-transform hover:scale-110"
                    onClick={handleCloseAttempt}
                />
                <div className="bg-white rounded-[12px] pt-8 px-8 pb-6 max-h-[85vh] overflow-y-auto edit-merchant-modal shadow-xl relative animate-[slideIn_0.3s_cubic-bezier(0.16,1,0.3,1)]">
                    <h2 className="text-[20px] md:text-[24px] font-[600] text-[#303030] font-poppins mb-6 pr-8 md:pr-0">Edit Merchant Details</h2>

                    <form onSubmit={formik.handleSubmit} className="flex flex-col gap-5">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                            <Input
                                label="Business Name"
                                labelClassName="!text-[16px] !font-[500] !mb-2"
                                name="businessName"
                                placeholder="Enter Business Name"
                                value={formik.values.businessName}
                                handleChange={formik.handleChange}
                                handleBlur={formik.handleBlur}
                                error={formik.errors.businessName}
                                touched={formik.touched.businessName}
                                width="100%"
                                radius="5px"
                                inputClass="!h-[45px] !rounded-[5px]"
                                InputDivClass="!mt-0"
                            />
                            <Selector
                                label="Business Category"
                                labelClassName="!text-[16px] !font-[500] !mb-2"
                                placeholder={<span className="text-[#767676] text-[14px]">Select Business Category</span>}
                                loading={categoriesLoading}
                                value={formik.values.category}
                                options={categories.map(cat => ({ label: cat.title, value: cat._id }))}
                                onChange={(val) => formik.setFieldValue("category", val)}
                                multiple={true}
                                divClassName="w-full edit-merchant-selector"
                                selectClassName="w-full !rounded-[5px] !min-h-[45px]"
                                mode="multiple"
                                suffixIcon={<DownArrow className="w-[12px] h-[12px]" />}
                                getPopupContainer={(triggerNode) => triggerNode.parentNode}
                            />
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                            <PhoneFormInput
                                label="Business Number"
                                labelClass="!text-[16px] !font-[500] !mb-2 font-poppins text-[#303030]"
                                name="contactPersonNumber"
                                value={formik.values.contactPersonNumber}
                                handleBlur={formik.handleBlur}
                                error={formik.errors.contactPersonNumber}
                                touched={formik.touched.contactPersonNumber}
                                setFieldValue={formik.setFieldValue}
                                containerClass="!rounded-[5px] !border-[#D9D9D9] !h-[45px]"
                                inputClass="!h-full !border-none !bg-transparent !rounded-[5px] !font-poppins !text-[14px] placeholder:!text-[#767676]"
                                buttonClass="!rounded-l-[5px] !border-none !bg-[#D9D9D9]"
                                placeholder="Enter Business Number"
                            />
                            <Input
                                label="Website URL"
                                labelClassName="!text-[16px] !font-[500] !mb-2"
                                name="website"
                                placeholder="Enter Website URL"
                                value={formik.values.website}
                                handleChange={formik.handleChange}
                                handleBlur={formik.handleBlur}
                                error={formik.errors.website}
                                touched={formik.touched.website}
                                width="100%"
                                radius="5px"
                                inputClass="!h-[45px] !rounded-[5px]"
                                InputDivClass="!mt-0"
                            />
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                            <Input
                                label="Contact Person Name"
                                labelClassName="!text-[16px] !font-[500] !mb-2"
                                name="contactPersonName"
                                placeholder="Contact Person Name"
                                value={formik.values.contactPersonName}
                                handleChange={formik.handleChange}
                                handleBlur={formik.handleBlur}
                                error={formik.errors.contactPersonName}
                                touched={formik.touched.contactPersonName}
                                width="100%"
                                radius="5px"
                                inputClass="!h-[45px] !rounded-[5px]"
                                InputDivClass="!mt-0"
                            />
                        </div>

                        {!formik.values.isOnlineMerchant && (
                            <div className="!mt-0">
                                <p className="font-poppins text-[16px] font-[500] text-[#303030] mb-2">Business Address</p>
                                <div className="h-[250px] w-full rounded-[8px] overflow-hidden border border-[#D9D9D9] relative box-border">
                                    <GoogleMaps
                                        onLocationSelect={handleLocationSelect}
                                        searchContainerClass="!w-[80%] !left-[10px] !top-[10px]"
                                        onBlur={() => formik.setFieldTouched("address", true)}
                                        defaultAddress={formik.values.address}
                                        placeholder="Add business address"
                                        searchBarDivClassName="w-full flex items-center h-[45px] relative"
                                        searchBarInputClassName="navSearchBar h-full w-[100%] ps-[3%] pe-[12%] text-[14px] font-[400] font-poppins outline-none bg-white text-[#303030] rounded-[5px] border border-[#D9D9D9] placeholder:!text-[#767676]"
                                        searchIconClassName="absolute right-[3%] w-[20px] h-[20px]"
                                        mapContainerStyle="!h-full !w-full !mt-0 !rounded-none relative"
                                        options={{
                                            mapTypeControl: false,
                                            streetViewControl: true,
                                            fullscreenControl: true
                                        }}
                                        defaultCenter={formik.values.locationLat && formik.values.locationLng ? { lat: formik.values.locationLat, lng: formik.values.locationLng } : undefined}
                                    />
                                </div>
                                {formik.touched.address && formik.errors.address && (
                                    <p className="text-red-600 text-[12px] font-[500] mt-1">{formik.errors.address}</p>
                                )}
                            </div>
                        )}

                        <div className="w-full p-4 bg-white border border-[#D9D9D9] rounded-[5px]">
                            <div className="flex items-center gap-2">
                                <input
                                    type="checkbox"
                                    id="isOnlineMerchant"
                                    checked={formik.values.isOnlineMerchant}
                                    onChange={(e) => {
                                        const checked = e.target.checked;
                                        formik.setFieldValue("isOnlineMerchant", checked);
                                        if (checked) {
                                            formik.setFieldValue("address", "Australia");
                                        } else {
                                            formik.setFieldValue("address", merchantDetails?.address || "");
                                        }
                                    }}
                                    className="peer hidden"
                                />
                                <label
                                    htmlFor="isOnlineMerchant"
                                    className={`w-5 h-5 border rounded-[3px] flex items-center justify-center cursor-pointer transition ${formik.values.isOnlineMerchant
                                        ? 'bg-[#00ADEF] border-[#00ADEF]'
                                        : 'border-[#00ADEF] bg-white'
                                        }`}
                                >
                                    {formik.values.isOnlineMerchant && (
                                        <svg
                                            className="w-4 h-4"
                                            fill="none"
                                            stroke="white"
                                            strokeWidth="3"
                                            viewBox="0 0 24 24"
                                        >
                                            <path d="M5 13l4 4L19 7" />
                                        </svg>
                                    )}
                                </label>
                                <label
                                    htmlFor="isOnlineMerchant"
                                    className="font-poppins text-[13px] md:text-[14px] text-[#303030] cursor-pointer select-none"
                                >
                                    I deal in online offers and don't have a physical address.
                                </label>
                            </div>

                            {formik.values.isOnlineMerchant && (
                                <div className="mt-3 overflow-hidden animate-[fadeIn_0.3s_ease]">
                                    <div className="p-3 bg-[#F9F9F9] border border-[#E5E5E5] rounded-[5px] cursor-not-allowed">
                                        <p className="font-poppins text-[13px] sm:text-[14px] text-[#767676] font-[400]">
                                            Region: Australia
                                        </p>
                                    </div>
                                </div>
                            )}
                        </div>

                        <InputTextArea
                            label="Business Description"
                            name="aboutUs"
                            placeholder="Business Description"
                            value={formik.values.aboutUs}
                            setFieldValue={formik.setFieldValue}
                            handleBlur={formik.handleBlur}
                            error={formik.errors.aboutUs}
                            touched={formik.touched.aboutUs}
                            textareaClassName="w-full !h-[120px] px-3 py-2 rounded-[5px] border border-[#D9D9D9] text-[#303030] font-poppins text-[14px] outline-none resize-none placeholder:text-[#767676]"
                            labelClassName="font-poppins text-[16px] font-[500] text-[#303030] mb-2"
                        />

                        <div className="flex gap-3 mt-4 w-full">
                            <button
                                type="button"
                                onClick={handleCloseAttempt}
                                className="flex-1 md:flex-none px-4 md:px-10 py-2 h-[45px] border border-[#00ADEF] rounded-[8px] text-[14px] md:text-[16px] font-[600] text-[#00ADEF] hover:bg-[#F0FAFF] transition-all whitespace-nowrap"
                            >
                                Cancel
                            </button>
                            <button
                                type="submit"
                                disabled={loading || !formik.dirty || !formik.isValid}
                                className="flex-1 md:flex-none px-4 md:px-10 py-2 h-[45px] bg-[#00ADEF] rounded-[8px] text-[14px] md:text-[16px] font-[600] text-white transition-all hover:bg-[#0088CC] disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 whitespace-nowrap"
                            >
                                {loading ? (
                                    <>
                                        <div className="w-[18px] h-[18px] border-2 border-white border-t-transparent rounded-full animate-spin" />
                                        Saving...
                                    </>
                                ) : "Save Changes"}
                            </button>
                        </div>
                    </form>
                </div>
            </div>

            {showExitConfirm && (
                <div className="absolute inset-0 z-[10002] flex items-center justify-center bg-black bg-opacity-50">
                    <div className="bg-white rounded-[10px] p-6 w-[335px] sm:w-[470px] shadow-xl flex flex-col items-center animate-[fadeIn_0.2s_ease-out]">
                        <h3 className="text-[18px] font-[500] text-[#303030] mb-2 font-poppins text-center">Discard Changes?</h3>
                        <p className="text-[13px] font-[400] text-[#8C8C8C] text-center mb-6 font-poppins max-w-[80%]">
                            You have unsaved changes. Are you sure you want to discard them and leave?
                        </p>
                        <div className="flex gap-4 w-full justify-center">
                            <button
                                onClick={() => setShowExitConfirm(false)}
                                className="h-[40px] w-[120px] border border-[#00ADEF] text-[#00ADEF] rounded-[6px] font-[500] font-poppins hover:bg-gray-50 transition-colors"
                            >
                                Stay
                            </button>
                            <button
                                onClick={onClose}
                                className="h-[40px] w-[120px] bg-[#00ADEF] text-white rounded-[6px] font-[500] font-poppins hover:bg-[#0088CC] transition-colors"
                            >
                                Leave
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default EditMerchantModal;
